import React, { useState, useEffect, useMemo } from 'react';
import { associateApi, skillMatrixApi } from '../lib/api';
import { useUnit } from '../contexts/UnitContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Search, History, Calculator } from 'lucide-react';

/**
 * The Associate Skill Matrix — where a level comes from.
 *
 *   associate → approved designation → the skills that designation requires
 *            → 5-S marked out of 16 by hand, each practical skill rated 1-4
 *            → obtained ÷ applicable maximum → L1/L2/L3/L4.
 *
 * HR enters marks. Nobody types a level: there is deliberately no level
 * control on this screen, because a level that can be typed is a level that
 * can be wrong in a way nothing downstream can detect.
 *
 * Three states per skill, and they are not interchangeable:
 *   BLANK — not assessed yet. Counts for nothing, and blocks saving.
 *   N/A   — does not apply to this job. Excluded from the obtained marks AND
 *           from the maximum, which is what makes it genuinely excluded
 *           rather than a zero in disguise.
 *   RATED — a number. Zero is a rating; blank is not.
 *
 * The total is WEIGHTED, not averaged: 5-S is worth 16 marks and a practical
 * skill 4, so averaging the eleven percentages would let one 4-mark topic
 * count as much as the whole 5-S paper. The working is printed beside the
 * result so it can be checked rather than trusted.
 */

const BANDS = [
  { from: 80, to: 100, level: 4, label: 'L4' },
  { from: 60, to: 80, level: 3, label: 'L3' },
  { from: 30, to: 60, level: 2, label: 'L2' },
  { from: 0, to: 30, level: 1, label: 'L1' },
];

const levelFor = (pct) => (pct >= 80 ? 4 : pct >= 60 ? 3 : pct >= 30 ? 2 : 1);
/* L4 is the top of the skill ladder. Champion is a separate recognition
   and is NOT shown here — holding L4 does not confer it. */
const levelLabel = (n) => `L${n}`;

/* What the four ratings mean. Shown on hover rather than printed beside every
   row — eleven copies of four sentences is not an assessment form. */
const RATINGS = [
  { value: 1, label: 'Basic knowledge', detail: 'Knows what the work involves but still needs real guidance.' },
  { value: 2, label: 'Works with supervision', detail: 'Does it correctly with a supervisor or senior alongside.' },
  { value: 3, label: 'Works independently', detail: 'Does it correctly and confidently with no regular supervision.' },
  { value: 4, label: 'Can teach others', detail: 'Works independently AND can explain, demonstrate and train others.' },
];

/**
 * ONE square, divided into four. Not four buttons in a row.
 *
 * A 2×2 grid inside a single bordered box: one outer border, one vertical
 * divider, one horizontal divider, no gaps. Clicking a quadrant selects THAT
 * RATING, and every quadrant up to it fills — click 3 and 1, 2, 3 all turn
 * green, because the rating is "works independently", not "the third box".
 * Clicking the one already selected clears back to not-assessed.
 */
function QuadRating({ value, onChange, disabled }) {
  const n = Number(value) || 0;
  const cell = (q) => {
    const on = n >= q;
    const r = RATINGS[q - 1];
    return (
      <button type="button" key={q} disabled={disabled}
              data-testid={`quad-${q}`} aria-label={`${q} — ${r.label}`}
              aria-pressed={on}
              title={disabled ? 'Not applicable' : `${q} — ${r.label}: ${r.detail}`}
              onClick={() => onChange(n === q ? null : q)}
              className={`w-[18px] h-[18px] transition-colors focus:outline-none
                          focus-visible:ring-2 focus-visible:ring-[#2563EB] focus-visible:z-10 ${
                            disabled ? 'bg-[#F2F4F8] cursor-not-allowed'
                              : on ? 'bg-[#15A36E] hover:bg-[#128A5D]'
                                   : 'bg-white hover:bg-[#EAF7F0]'}`} />
    );
  };
  return (
    <span data-testid="quad-rating"
          className={`inline-grid grid-cols-2 grid-rows-2 rounded-[5px] overflow-hidden
                      border border-[#C9CFDB] divide-x divide-y divide-[#C9CFDB] align-middle ${
                        disabled ? 'opacity-50' : ''}`}>
      {[1, 2, 3, 4].map(cell)}
    </span>
  );
}

function LevelPill({ level, big = false }) {
  if (!level) return <span className="text-[#C9CFDC]">—</span>;
  const top = Number(level) === 4;
  return (
    <span className={`inline-flex items-center rounded-full border font-semibold ${
      big ? 'px-3 py-1 text-sm' : 'px-2 py-0.5 text-[11px]'} ${
      top ? 'bg-[#FFF4E5] text-[#8A4B00] border-[#FFD9A8]'
             : 'bg-[#EFF3FB] text-[#2F4A77] border-[#D9E2F3]'}`}>
      {levelLabel(Number(level))}
    </span>
  );
}

export default function SkillMatrix() {
  const { activeUnit, isAllUnits } = useUnit();
  const { hasPerm } = useAuth();
  const canAssess = hasPerm('associate.edit');

  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [picked, setPicked] = useState(null);     // the loaded assessment payload
  const [marks, setMarks] = useState({});         // key → '' | number as text
  const [busy, setBusy] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [mapPending, setMapPending] = useState([]);

  // Who still has no level — the working list for this screen. Searching is
  // server-side so it covers the whole register, not a page of it.
  const search = React.useCallback(() => {
    associateApi.list({ unit: isAllUnits ? undefined : activeUnit, q, limit: 40 })
      .then((r) => setRows(r.data?.rows || []))
      .catch(() => setRows([]));
  }, [q, activeUnit, isAllUnits]);
  useEffect(() => { const t = setTimeout(search, 250); return () => clearTimeout(t); }, [search]);

  useEffect(() => {
    skillMatrixApi.map()
      .then((r) => setMapPending((r.data?.rows || []).filter((x) => x.pending)))
      .catch(() => setMapPending([]));
  }, []);

  const open = async (row) => {
    setShowHistory(false);
    try {
      const r = await associateApi.assessment(row.id);
      setPicked(r.data);
      // Seed the form from the last assessment, so a retest is a correction of
      // what was recorded rather than a blank sheet typed from memory.
      const last = r.data.latest;
      const m = {};
      (r.data.required?.matrices || []).forEach((mx) => {
        const prev = last?.scored?.find((s) => s.key === mx.key);
        m[mx.key] = prev ? String(prev.marks) : '';
      });
      setMarks(m);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not open the assessment');
    }
  };

  // The same arithmetic the server does, shown live so HR can see the level
  // move as they type. The SERVER's result is what gets saved — this is a
  // preview of it, never the source of it.
  const live = useMemo(() => {
    const mx = picked?.required?.matrices || [];
    if (!mx.length) return null;
    // THE DENOMINATOR IS EVERY MAPPED SKILL, not just the filled-in ones.
    // It is fixed the moment the designation's mapping is known: five skills
    // out of 4 each is out of 20 whether one is scored or all five. Summing
    // only what was entered let a part-filled sheet read 100%.
    const possible = mx.reduce((t, m) => t + (Number(m.max_marks) || 0), 0);
    let obtained = 0, entered = 0;
    for (const m of mx) {
      const raw = (marks[m.key] ?? '').toString().trim();
      if (raw === '') continue;                      // blank is not zero
      const v = Number(raw);
      if (Number.isNaN(v)) continue;
      obtained += Math.min(Math.max(v, 0), Number(m.max_marks) || 0);
      entered += 1;
    }
    const avg = possible ? Math.round(obtained * 10000 / possible) / 100 : 0;
    return { average: avg, level: levelFor(avg), obtained, possible,
             entered, total: mx.length, complete: entered === mx.length };
  }, [picked, marks]);

  const remaining = (live?.total || 0) - (live?.entered || 0);

  const save = async () => {
    if (!picked) return;
    // Short and actionable. The server validates independently; naming every
    // missing skill here produced a paragraph nobody read.
    if (remaining > 0) {
      toast.error('Complete all required skill assessments.');
      return;
    }
    setBusy(true);
    try {
      // No N/A: every mapped skill is scored. A skill that does not apply
      // is removed from the designation's mapping in Masters instead.
      const entries = (picked.required.matrices || []).map(
        (m) => ({ key: m.key, marks: Number(marks[m.key]) }));
      const r = await associateApi.saveAssessment(picked.associate.id,
        { entries, assessed_on: new Date().toISOString().slice(0, 10) });
      toast.success(`${picked.associate.name}: ${r.data.average_percent}% → ${levelLabel(r.data.level)}`);
      await open(picked.associate);
      search();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save the assessment');
    } finally { setBusy(false); }
  };

  const cell = 'border border-[#E6E9F0] rounded-lg px-2 py-1.5 text-sm w-24 text-right tabular-nums';

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-5" data-testid="skill-matrix-page">
      {/* ---- who to assess ---- */}
      <div className="bg-white border border-[#E6E9F0] rounded-xl p-4 h-fit">
        <div className="relative mb-3">
          <Search className="w-4 h-4 text-[#98A1B2] absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-9"
                 placeholder="Search name, code or designation…"
                 data-testid="skill-search" />
        </div>
        <div className="max-h-[520px] overflow-auto divide-y divide-[#F2F4F8]">
          {rows.length === 0 && <div className="text-sm text-[#98A1B2] py-3">No associates match.</div>}
          {rows.map((r) => (
            <button key={r.id} onClick={() => open(r)} data-testid={`skill-pick-${r.code}`}
                    className={`w-full text-left py-2 px-1 hover:bg-[#F7F8FB] ${
                      picked?.associate?.id === r.id ? 'bg-[#F1F5FD]' : ''}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm text-[#11182B] truncate">{r.name}</span>
                <LevelPill level={r.level} />
              </div>
              <div className="text-[11px] text-[#98A1B2] truncate">
                {r.code} · {r.designation || '—'}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* ---- the assessment ---- */}
      <div className="bg-white border border-[#E6E9F0] rounded-xl p-5">
        {!picked ? (
          <div className="text-sm text-[#5A6377]">
            Pick an associate to assess. Their designation decides which Skill Matrices are
            scored — the level is calculated from the marks and cannot be typed.
            {mapPending.length > 0 && (
              <div data-testid="skill-map-pending"
                   className="mt-4 text-[12px] bg-[#FFF7E6] border border-[#FDD08A] text-[#8A5A12] rounded-lg px-3 py-2">
                <b>Mapping Pending — {mapPending.length} designation(s)</b> have no Skill Matrices
                configured: {mapPending.map((m) => m.designation).join(', ')}. Anybody holding one
                of those cannot be assessed until an admin sets their matrices in
                Settings → Associate Skill Matrix. They are not guessed here.
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-start justify-between gap-3 pb-4 border-b border-[#F2F4F8]">
              <div>
                <div className="text-lg font-semibold text-[#11182B]">{picked.associate.name}</div>
                <div className="text-sm text-[#5A6377]">
                  {picked.associate.code} · {picked.associate.designation || '—'}
                  {picked.associate.department ? ` · ${picked.associate.department}` : ''}
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-wide text-[#98A1B2]">Current level</div>
                  <LevelPill level={picked.associate.level} big />
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-wide text-[#98A1B2]">Last assessment</div>
                  <div className="text-sm text-[#11182B]">
                    {picked.latest
                      ? `${picked.latest.average_percent}% · ${(picked.latest.assessed_on || picked.latest.assessed_at || '').slice(0, 10)}`
                      : 'Not assessed'}
                  </div>
                </div>
              </div>
            </div>

            {picked.required.pending ? (
              <div data-testid="skill-required-pending"
                   className="mt-4 text-sm bg-[#FFF7E6] border border-[#FDD08A] text-[#8A5A12] rounded-lg px-3 py-2">
                {picked.required.why}
              </div>
            ) : (
              <>
                <div className="mt-4 text-[11px] uppercase tracking-wide text-[#98A1B2] mb-2">
                  Required Skill Matrices — {picked.associate.designation}
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[11px] uppercase text-[#98A1B2] border-b border-[#F2F4F8]">
                      <th className="text-left py-2">Skill Matrix</th>
                      <th className="text-right py-2 w-28">Marks / rating</th>
                      <th className="text-right py-2 w-20">%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {picked.required.matrices.map((m) => {
                      const top = Number(m.max_marks) || 100;
                      return (
                        <tr key={m.key} className="border-b border-[#F7F8FB]"
                            data-testid={`skill-row-${m.key}`}>
                          <td className="py-2 text-[#11182B]">
                            {m.name}
                            <span className="text-[11px] text-[#98A1B2]"> / {top}</span>
                          </td>
                          <td className="py-2 text-right">
                            {/* 5-S is a question paper marked by hand, out of
                                16. The ten practical skills are a judgement
                                about how somebody works, out of 4 — so they
                                get the rating square, not a number box. */}
                            {m.method === 'quad' ? (
                              <span className="inline-flex justify-end w-full pr-1">
                                <QuadRating
                                  value={marks[m.key]}
                                  disabled={!canAssess}
                                  onChange={(v) => setMarks((s) => ({ ...s, [m.key]: v == null ? '' : String(v) }))} />
                              </span>
                            ) : (
                              <input type="number" min={0} max={top} step="0.5"
                                     disabled={!canAssess}
                                     value={marks[m.key] ?? ''}
                                     onChange={(e) => setMarks((s) => ({ ...s, [m.key]: e.target.value }))}
                                     data-testid={`skill-marks-${m.key}`}
                                     className={cell} />
                            )}
                          </td>
                          <td className="py-2 text-right tabular-nums text-[#5A6377] whitespace-nowrap">
                            {String(marks[m.key] ?? '').trim() === ''
                              ? <span className="text-[#C9CFDC]">—</span>
                              : `${marks[m.key]}/${top}`}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <div className="mt-4 flex flex-wrap items-center justify-between gap-4 bg-[#F7F8FB]
                                border border-[#EEF1F6] rounded-xl px-4 py-3">
                  <div className="flex items-center gap-6 flex-wrap">
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-[#98A1B2]">Total obtained</div>
                      <div className="text-xl font-bold text-[#172554] tabular-nums"
                           data-testid="skill-obtained">
                        {live ? `${live.obtained}/${live.possible}` : '—'}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-[#98A1B2]">Overall score</div>
                      <div className="text-xl font-bold text-[#172554] tabular-nums"
                           data-testid="skill-average">
                        {live ? `${live.average}%` : '—'}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-[#98A1B2]">Calculated level</div>
                      <div data-testid="skill-level">
                        {live ? <LevelPill level={live.level} big /> : <span className="text-[#C9CFDC]">—</span>}
                      </div>
                    </div>
                    {/* Status, not an essay. The old block listed every
                        missing skill by name and ran to three lines; the
                        count says the same thing and the rows above already
                        show which are blank. Validation itself is unchanged
                        and still enforced by the server. */}
                    <div className="text-[11px] max-w-[220px]">
                      {remaining > 0 ? (
                        <span data-testid="skill-incomplete" className="text-[#B14A0E]">
                          Complete all required skill assessments
                          <span className="block text-[#98A1B2]">{remaining} of {live?.total} remaining</span>
                        </span>
                      ) : (
                        <span data-testid="skill-complete" className="text-[#15654B]">
                          ✓ All {live?.total} skills assessed
                        </span>
                      )}
                    </div>
                  </div>
                  {canAssess && (
                    <Button onClick={save} disabled={busy || !live || remaining > 0}
                            data-testid="skill-save">
                      {busy ? 'Saving…' : 'Calculate & save assessment'}
                    </Button>
                  )}
                </div>

                <div className="mt-3 text-[11px] text-[#98A1B2]">
                  {BANDS.map((b) => `${b.from}–${b.to === 100 ? '100' : `<${b.to}`}% ${b.label}`).join(' · ')}
                </div>
              </>
            )}

            {/* ---- history ---- */}
            <div className="mt-5 pt-4 border-t border-[#F2F4F8]">
              <button onClick={() => setShowHistory((v) => !v)} data-testid="skill-history-toggle"
                      className="text-sm font-medium text-[#2563EB] inline-flex items-center gap-1">
                <History className="w-4 h-4" />
                Assessment history ({picked.history.length})
              </button>
              {showHistory && (
                picked.history.length === 0
                  ? <div className="text-sm text-[#98A1B2] mt-2">No assessment recorded yet.</div>
                  : (
                    <table className="w-full text-sm mt-3">
                      <thead>
                        <tr className="text-[11px] uppercase text-[#98A1B2] border-b border-[#F2F4F8]">
                          <th className="text-left py-2">Date</th>
                          <th className="text-left py-2">Assessor</th>
                          <th className="text-right py-2">Scored</th>
                          <th className="text-right py-2">N/A</th>
                          <th className="text-right py-2">Average</th>
                          <th className="text-right py-2">Level</th>
                        </tr>
                      </thead>
                      <tbody>
                        {picked.history.map((h, i) => (
                          <tr key={h.id} className="border-b border-[#F7F8FB]">
                            <td className="py-1.5 text-[#11182B]">
                              {(h.assessed_on || h.assessed_at || '').slice(0, 10)}
                              {i === 0 && <span className="ml-2 text-[10px] font-semibold text-[#15654B]">CURRENT</span>}
                            </td>
                            <td className="py-1.5 text-[#5A6377]">{h.assessor || '—'}</td>
                            <td className="py-1.5 text-right tabular-nums">{h.applicable_count}</td>
                            <td className="py-1.5 text-right tabular-nums text-[#98A1B2]">{h.na_count}</td>
                            <td className="py-1.5 text-right tabular-nums font-semibold">{h.average_percent}%</td>
                            <td className="py-1.5 text-right"><LevelPill level={h.level} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
